// src/components/FilterSelect.jsx - Versión corregida
import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown, Form, Badge } from 'react-bootstrap';
import { FaFilter, FaTimes } from 'react-icons/fa';

/**
 * Componente reutilizable de filtro con selección múltiple mediante checkboxes
 * 
 * @param {Array} options - Lista de opciones de filtro ({ id, label, value, checked })
 * @param {Array} activeFilters - Filtros activos actualmente
 * @param {Function} onFiltersChange - Callback cuando cambian los filtros
 * @param {Function} onOptionsUpdate - Callback cuando se actualizan las opciones
 * @param {string} title - Título del filtro
 * @param {React.ReactNode} icon - Icono personalizado (opcional)
 * @param {string} variant - Variante de Bootstrap para el botón (opcional)
 * @param {string} filterKey - Clave única para identificar este filtro (NUEVO)
 */
function FilterSelect({ 
  options = [], 
  activeFilters = [], 
  onFiltersChange, 
  onOptionsUpdate, 
  title, 
  icon, 
  variant = "outline-secondary",
  filterKey // NUEVO: identificador único para esta instancia
}) {
  const [show, setShow] = useState(false);
  
  // Generar IDs únicos para esta instancia usando filterKey
  const uniqueOptions = useMemo(() => {
    return options.map(opt => ({
      ...opt,
      // Crear ID único combinando filterKey con el ID original
      uniqueId: `${filterKey}-${opt.id}`,
      // Preservar el ID original para comparaciones
      originalId: opt.id
    }));
  }, [options, filterKey]);

  // Estado local para los checkboxes con IDs únicos
  const [localOptions, setLocalOptions] = useState([]);
  
  // Sincronizar opciones externas con el estado local
  useEffect(() => {
    if (!uniqueOptions.length) return;
    
    // Actualizar opciones locales con el estado checked sincronizado desde activeFilters
    // Solo comparar con activeFilters que pertenecen a esta instancia
    const updatedOptions = uniqueOptions.map(opt => ({
      ...opt,
      // Comparar usando el ID original, pero solo con filtros de esta instancia
      checked: activeFilters.some(filter => 
        filter.originalId === opt.originalId || filter.id === opt.originalId
      )
    }));
    
    setLocalOptions(updatedOptions);
  }, [uniqueOptions, activeFilters]);

  // Manejar selección/deselección de "Todos"
  const handleToggleAll = () => {
    const allChecked = localOptions.length > 0 && localOptions.every(opt => opt.checked);
    
    // Actualizar estado local inmediatamente para mejor respuesta UI
    const newLocalOptions = localOptions.map(opt => ({ 
      ...opt, 
      checked: !allChecked 
    }));
    setLocalOptions(newLocalOptions);
    
    // Crear filtros con el formato esperado por el padre
    const newActiveFilters = allChecked ? [] : newLocalOptions.map(opt => ({
      id: opt.originalId, // Usar ID original
      label: opt.label,
      value: opt.value,
      originalId: opt.originalId,
      filterKey: filterKey // Identificar de qué filtro proviene
    }));
    
    // Propagar cambios hacia arriba
    onOptionsUpdate(newLocalOptions);
    onFiltersChange(newActiveFilters);
  };

  // Manejar cambio en un filtro específico
  const handleToggleFilter = (filter) => {
    // Actualizar estado local inmediatamente para mejor respuesta UI
    const newLocalOptions = localOptions.map(opt => {
      if (opt.uniqueId === filter.uniqueId) {
        return { ...opt, checked: !opt.checked };
      }
      return opt;
    });
    setLocalOptions(newLocalOptions);
    
    // Crear filtros activos con el formato esperado
    const newActiveFilters = newLocalOptions
      .filter(opt => opt.checked)
      .map(opt => ({
        id: opt.originalId, // Usar ID original
        label: opt.label,
        value: opt.value,
        originalId: opt.originalId,
        filterKey: filterKey // Identificar de qué filtro proviene
      }));
    
    // Propagar cambios hacia arriba
    onOptionsUpdate(newLocalOptions);
    onFiltersChange(newActiveFilters);
  };

  // Mostrar los filtros activos como badges
  const renderActiveBadges = () => {
    if (activeFilters.length === 0) return null;
    
    return (
      <div className="d-flex flex-wrap mt-2 gap-1">
        {activeFilters.map(filter => {
          // Encontrar la opción local correspondiente para el toggle
          const localOption = localOptions.find(opt => 
            opt.originalId === filter.originalId || opt.originalId === filter.id
          );
          
          return (
            <Badge 
              key={`${filterKey}-badge-${filter.id}`} 
              bg="secondary" 
              className="d-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => localOption && handleToggleFilter(localOption)}
            >
              {filter.label} <FaTimes className="ms-1" size={10} />
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Dropdown show={show} onToggle={(isOpen) => setShow(isOpen)}>
        <Dropdown.Toggle 
          variant={variant} 
          size="sm" 
          id={`filter-dropdown-${filterKey}-${title}`}
        >
          {icon || <FaFilter className="me-1" />}
          <span className="ms-1">{title}</span> 
          {activeFilters.length > 0 && (
            <Badge bg="primary" pill className="ms-1">{activeFilters.length}</Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu style={{ minWidth: '250px', padding: '0.5rem' }}>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Form.Check
              type="checkbox"
              id={`filter-all-${filterKey}`}
              label="Seleccionar todos"
              checked={localOptions.length > 0 && localOptions.every(opt => opt.checked)}
              onChange={handleToggleAll}
              className="mb-2 border-bottom pb-2"
            />
            
            {localOptions.map((filter) => (
              <Form.Check
                key={filter.uniqueId}
                type="checkbox"
                id={filter.uniqueId}
                label={filter.label}
                checked={filter.checked}
                onChange={() => handleToggleFilter(filter)}
                className="mb-1"
              />
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
      
      {renderActiveBadges()}
    </div>
  );
}

export default FilterSelect;